import { useContext } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { request, type TOptions } from './request';
import type { TApiResponse } from '@shared/types';
import { queryClient } from '@core/api/query-client';
import { objectToFormData } from '@shared/utils/helpers/convert-obj-to-form-data';
import { showToast } from '@src/shared/utils/helpers/show-toast';
import {
  TableContext,
  type TiclickTableContextType,
} from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context';

type TUpdateBody<B> = Partial<B> & {
  id: number | string;
  /**
   * Optional metadata for client-side use only (not sent to the API).
   * Useful for optimistic UI updates or storing additional context.
   *
   * @example
   * ```ts
   * updateLead({
   *   id: 123,
   *   referal: 456,
   *   tanstackMeta: { referalName: 'John Doe' }
   * })
   * // Access via: variables?.tanstackMeta?.referalName
   * ```
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tanstackMeta?: any;
};

type TFetchOneBody = { id?: string | number | null };

type TDeleteBody = {
  id: number | string;
};

type TBulkDeleteBody = {
  ids: (number | string)[];
};

type TCreateCrudService<
  T extends Record<string, unknown>,
  Q extends Record<string, unknown>,
  B,
> = {
  queryKey: string[];
  url: string;
  factory?: {
    fetch?: {
      tanstackOptions?: Partial<UseQueryOptions<TApiResponse<T>>>;
      requestOptions?: (params?: Partial<Q>) => TOptions;
    };

    infiniteQuery?: {
      tanstackOptions?: Partial<
        UseInfiniteQueryOptions<
          TApiResponse<T>,
          Error,
          InfiniteData<TApiResponse<T>>,
          readonly unknown[],
          number
        >
      >;
      requestOptions?: (params?: Partial<Q>) => TOptions;
    };

    fetchOne?: {
      tanstackOptions?: Partial<UseQueryOptions<T>>;
      requestOptions?: (body?: TFetchOneBody) => TOptions;
    };

    create?: {
      tanstackOptions?: Partial<UseMutationOptions<T, Error, B, unknown>>;
      requestOptions?: (body?: B) => TOptions;
      customOptions?: Record<string, unknown>;
    };

    update?: {
      tanstackOptions?: Partial<
        UseMutationOptions<T, Error, TUpdateBody<B>, unknown>
      >;
      requestOptions?: (body?: TUpdateBody<B>) => TOptions;
      customOptions?: Record<string, unknown>;
    };

    delete?: {
      tanstackOptions?: Partial<
        UseMutationOptions<T, Error, TDeleteBody, unknown>
      >;
      requestOptions?: (body?: TDeleteBody) => TOptions;
      customOptions?: Record<string, unknown>;
    };

    bulkDelete?: {
      tanstackOptions?: Partial<
        UseMutationOptions<T, Error, TBulkDeleteBody, unknown>
      >;
      requestOptions?: (options?: TBulkDeleteBody) => TOptions;
      customOptions?: Record<string, unknown>;
    };

    bulkCreate?: {
      tanstackOptions?: Partial<UseMutationOptions<T, Error, B[], unknown>>;
      requestOptions?: (body?: B[]) => TOptions;
      customOptions?: Record<string, unknown>;
    };

    bulkUpdate?: {
      tanstackOptions?: Partial<UseMutationOptions<T, Error, B[], unknown>>;
      requestOptions?: (body?: B[]) => TOptions;
      customOptions?: Record<string, unknown>;
    };
  };
  validateQuery?: string[];
};

const createCrudService = <
  T extends Record<string, unknown>,
  Q extends Record<string, unknown>,
  B extends Record<string, unknown> = Record<string, unknown>,
>({
  queryKey,
  url,
  factory,
  validateQuery = [],
}: TCreateCrudService<T, Q, B>) => {
  const staleTime = 60000;

  const predicate = (q: Query) => {
    const queriesToRevalidate = [...queryKey, ...validateQuery];
    return queriesToRevalidate.some(
      (query) => Array.isArray(q.queryKey) && q.queryKey[0] === query
    );
  };

  const generateBodyAccToType = (body: Record<string, unknown> = {}) => {
    const hasFile = Object.values(body).some((value) => {
      return value instanceof File;
    });
    return hasFile ? objectToFormData(body) : body;
  };

  const invalidateOnMutation = (
    table: TiclickTableContextType<unknown> | null
  ) => {
    if (table) {
      table.table.options.meta?.queryData?.refetch();
    }

    queryClient.invalidateQueries(
      {
        predicate,
      },
      { cancelRefetch: false }
    );
  };

  return {
    useFetch: <R = TApiResponse<T>>(
      params?: Partial<Q>,
      options?: Partial<UseQueryOptions<R>>
    ) =>
      useQuery<R>({
        queryKey: [...queryKey, params],
        queryFn: async () =>
          await request<R>({
            url: `${url}/`,
            params,
            ...factory?.fetch?.requestOptions?.(params),
          }),
        staleTime,
        ...factory?.fetch?.tanstackOptions,
        ...options,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any),

    useFetchOne: (
      { id }: TFetchOneBody,
      options?: Partial<UseQueryOptions<T>>
    ) => {
      return useQuery({
        queryKey: [...queryKey.concat('single'), id],
        queryFn: async () =>
          await request<T>({
            url: `${url}/${id}/`,
            ...factory?.fetchOne?.requestOptions?.({ id }),
          }),
        staleTime,
        enabled: !!id,

        placeholderData: () => {
          const cached = queryClient
            .getQueriesData({
              queryKey,
              exact: false,
            })
            .filter(([k]) => !JSON.stringify(k).includes('single'))
            .find(([, data]) => !!data)?.[1];

          if (!cached) return undefined;

          if (
            typeof cached === 'object' &&
            'pages' in cached &&
            Array.isArray(cached.pages)
          ) {
            return cached.pages
              .flatMap((page) => page.results || [])
              .find((item) => String(item.id) === String(id));
          }

          if (
            typeof cached === 'object' &&
            'results' in cached &&
            Array.isArray(cached.results)
          ) {
            return cached.results.find(
              (item) => String(item.id) === String(id)
            );
          }

          return undefined;
        },
        ...factory?.fetchOne?.tanstackOptions,
        ...options,
      });
    },

    useFetchInfinite: (
      params?: Partial<Q>,
      options?: { enabled?: boolean }
    ) => {
      return useInfiniteQuery<
        TApiResponse<T>,
        Error,
        InfiniteData<TApiResponse<T>>,
        readonly unknown[],
        number
      >({
        queryKey: [...queryKey.concat('infinite'), params],
        queryFn: ({ pageParam }): Promise<TApiResponse<T>> =>
          request<TApiResponse<T>>({
            url: `${url}/`,
            params: { page_number: pageParam, ...params },
            ...factory?.infiniteQuery?.requestOptions?.(params),
          }),
        staleTime,
        getNextPageParam: (lastPage) => {
          return lastPage.next ? lastPage.page_number + 1 : undefined;
        },
        initialPageParam: 1,
        enabled: options?.enabled,
        ...factory?.infiniteQuery?.tanstackOptions,
      });
    },

    useCreate: () => {
      const table = useContext(TableContext);

      const { onSettled, ...restTanstackOptions } =
        factory?.create?.tanstackOptions || {};

      return useMutation({
        mutationFn: async (body?: B) => {
          return await request<T>({
            url: `${url}/`,
            body: generateBodyAccToType(body),
            method: 'POST',
            ...factory?.create?.requestOptions?.(body),
          });
        },

        onSuccess: (res) => {
          if (factory?.create?.customOptions?.disableSuccessToast) return;

          const description =
            typeof res?.detail === 'string'
              ? res.detail
              : 'Data has been created successfully';

          showToast({
            title: 'Creation Success',
            description,
            variant: 'success',
          });
        },

        onSettled: (...args) => {
          invalidateOnMutation(table);
          onSettled?.(...args);
        },

        ...restTanstackOptions,
      });
    },

    useUpdate: () => {
      const table = useContext(TableContext);

      const { onSettled, ...restTanstackOptions } =
        factory?.update?.tanstackOptions || {};

      return useMutation({
        mutationFn: async (updateBody: TUpdateBody<B>) => {
          return await request<T>({
            url: `${url}/${updateBody.id}/`,
            method: 'PATCH',
            body: generateBodyAccToType(updateBody),
            ...factory?.update?.requestOptions?.(updateBody),
          });
        },

        onSuccess: () => {
          if (factory?.update?.customOptions?.disableSuccessToast) return;

          showToast({
            title: 'Update Success',
            description: 'Data has updated successfully',
            variant: 'success',
          });
        },

        onSettled: (...args) => {
          invalidateOnMutation(table);
          onSettled?.(...args);
        },

        ...restTanstackOptions,
      });
    },

    useDelete: () => {
      const table = useContext(TableContext);

      const { onSettled, ...restTanstackOptions } =
        factory?.delete?.tanstackOptions || {};

      return useMutation({
        mutationFn: async ({
          id,
          requestOptions,
        }: {
          id: number | string;
          requestOptions?: TOptions;
        }) =>
          await request<T>({
            url: `${url}/${id}/`,
            method: 'DELETE',

            ...factory?.delete?.requestOptions?.({ id }),
            ...requestOptions,
          }),

        onSuccess: () => {
          if (factory?.delete?.customOptions?.disableSuccessToast) return;

          showToast({
            title: 'Delete Success',
            description: 'Data has deleted successfully',
            variant: 'success',
          });
        },

        onSettled: (...args) => {
          invalidateOnMutation(table);
          onSettled?.(...args);
        },
        ...restTanstackOptions,
      });
    },

    useBulkDelete: () => {
      const table = useContext(TableContext);

      const { onSettled, ...restTanstackOptions } =
        factory?.bulkDelete?.tanstackOptions || {};

      return useMutation({
        mutationFn: async ({ ids }: TBulkDeleteBody) => {
          return await request<T>({
            url: `${url}/bulk-delete/`,
            method: 'DELETE',
            body: { ids },

            ...factory?.bulkDelete?.requestOptions?.({ ids }),
          });
        },

        onSuccess: () => {
          if (factory?.bulkDelete?.customOptions?.disableSuccessToast) return;

          showToast({
            title: 'Bulk Delete Success',
            description: 'All selected data has been deleted successfully',
            variant: 'success',
          });
        },

        onSettled: (...args) => {
          invalidateOnMutation(table);
          onSettled?.(...args);
        },
        ...restTanstackOptions,
      });
    },

    useBulkCreate: () => {
      const table = useContext(TableContext);

      const { onSettled, ...restTanstackOptions } =
        factory?.bulkCreate?.tanstackOptions || {};

      return useMutation({
        mutationFn: async (body?: B[]) => {
          return await request<T>({
            url: `${url}/bulk-create/`,
            body,
            method: 'POST',
            ...factory?.bulkCreate?.requestOptions?.(body),
          });
        },

        onSuccess: (res) => {
          if (factory?.bulkCreate?.customOptions?.disableSuccessToast) return;
          const description =
            typeof res?.detail === 'string'
              ? res.detail
              : 'Data has been bulk created successfully';

          showToast({
            title: 'Bulk Creation Success',
            description,
            variant: 'success',
          });
        },

        onSettled: (...args) => {
          invalidateOnMutation(table);
          onSettled?.(...args);
        },

        ...restTanstackOptions,
      });
    },

    useBulkUpdate: () => {
      const table = useContext(TableContext);

      const { onSettled, ...restTanstackOptions } =
        factory?.bulkUpdate?.tanstackOptions || {};

      return useMutation({
        mutationFn: async (body?: B[]) => {
          return await request<T>({
            url: `${url}/bulk-update/`,
            body,
            method: 'POST',
            ...factory?.bulkUpdate?.requestOptions?.(body),
          });
        },

        onSuccess: (res) => {
          if (factory?.bulkUpdate?.customOptions?.disableSuccessToast) return;
          const description =
            typeof res?.detail === 'string'
              ? res.detail
              : 'Data has been bulk updated successfully';

          showToast({
            title: 'Bulk Update Success',
            description,
            variant: 'success',
          });
        },

        onSettled: (...args) => {
          invalidateOnMutation(table);
          onSettled?.(...args);
        },

        ...restTanstackOptions,
      });
    },
  };
};

export default createCrudService;
